import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc, getDocs, collection } from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiCheck, FiPlus, FiTrash2, FiMove, FiInfo } from "react-icons/fi";

// Default fixed header links that always show (cannot be removed from here, but can be toggled)
const FIXED_HEADER_LINKS = [
  { label: "HOME", path: "/" },
  { label: "MENTAL HEALTH RESOURCES", path: "/mental-health-resources" },
  { label: "SELF-CARE", path: "/self-care" },
  { label: "KINDNESS SHOP", path: "/shop" },
  { label: "AUTHOR'S CORNER", path: "/author-corner" },
  { label: "EVENTS", path: "/events" },
  { label: "BLOG", path: "/blog" },
];

const FIXED_FOOTER_LINKS = [
  { label: "Contact Us", path: "/contact" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Disclaimer", path: "/disclaimer" },
  { label: "Refund Policy", path: "/refund" },
  { label: "Terms & Conditions", path: "/terms-and-conditions" },
  { label: "About", path: "/about" },
];

const NavigationManager = () => {
  const [pages, setPages] = useState([]);
  const [headerNav, setHeaderNav] = useState([]);
  const [footerNav, setFooterNav] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customHeaderLink, setCustomHeaderLink] = useState({ label: "", path: "" });
  const [customFooterLink, setCustomFooterLink] = useState({ label: "", path: "" });

  useEffect(() => {
    const load = async () => {
      try {
        // Load custom pages
        const pagesSnap = await getDocs(collection(db, "pages"));
        const allPages = pagesSnap.docs.map((d) => ({
          id: d.id, label: d.data().title, path: `/page/${d.data().slug}`,
          isCustomPage: true,
        }));
        setPages(allPages);

        // Load saved navigation
        const navDoc = await getDoc(doc(db, "settings", "navigation"));
        if (navDoc.exists()) {
          const data = navDoc.data();
          setHeaderNav(data.header || []);
          setFooterNav(data.footer || []);
        }
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, []);

  const isInHeader = (path) => headerNav.some((n) => n.path === path);
  const isInFooter = (path) => footerNav.some((n) => n.path === path);

  const toggleHeader = (item) => {
    setHeaderNav((prev) =>
      isInHeader(item.path)
        ? prev.filter((n) => n.path !== item.path)
        : [...prev, { label: item.label, path: item.path }]
    );
  };

  const toggleFooter = (item) => {
    setFooterNav((prev) =>
      isInFooter(item.path)
        ? prev.filter((n) => n.path !== item.path)
        : [...prev, { label: item.label, path: item.path }]
    );
  };

  const removeFromHeader = (path) => setHeaderNav((prev) => prev.filter((n) => n.path !== path));
  const removeFromFooter = (path) => setFooterNav((prev) => prev.filter((n) => n.path !== path));

  const addCustomHeader = () => {
    if (!customHeaderLink.label || !customHeaderLink.path) return;
    setHeaderNav((prev) => [...prev, { label: customHeaderLink.label, path: customHeaderLink.path, isCustom: true }]);
    setCustomHeaderLink({ label: "", path: "" });
  };

  const addCustomFooter = () => {
    if (!customFooterLink.label || !customFooterLink.path) return;
    setFooterNav((prev) => [...prev, { label: customFooterLink.label, path: customFooterLink.path, isCustom: true }]);
    setCustomFooterLink({ label: "", path: "" });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "navigation"), { header: headerNav, footer: footerNav }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const moveUp = (arr, setArr, idx) => {
    if (idx === 0) return;
    const copy = [...arr];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    setArr(copy);
  };

  const moveDown = (arr, setArr, idx) => {
    if (idx === arr.length - 1) return;
    const copy = [...arr];
    [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
    setArr(copy);
  };

  const allAvailableItems = [
    ...FIXED_HEADER_LINKS,
    ...FIXED_FOOTER_LINKS,
    ...pages,
  ].filter((item, idx, self) => self.findIndex((i) => i.path === item.path) === idx);

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Navigation Manager</h1>
            <p className="text-sm text-gray-400">Control which pages appear in header and footer menus</p>
          </div>
          <div className="flex items-center gap-3">
            {saved && <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold"><FiCheck />Saved!</span>}
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Navigation"}
            </button>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex gap-3">
          <FiInfo className="text-amber-500 mt-0.5 shrink-0" size={16} />
          <div className="text-[13px] text-amber-700">
            <strong>Note:</strong> The current header links (HOME, MENTAL HEALTH RESOURCES, etc.) are kept as-is by default. 
            Use this manager to add extra pages to the header or footer. 
            The <strong>extra links</strong> you add here appear at the end of the header/footer. 
            To fully replace header nav, edit <code className="bg-amber-100 px-1 rounded">Header.js</code>.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* === HEADER NAV === */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 border-b px-5 py-4">
              <h2 className="text-[14px] font-extrabold">Header Navigation</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">Extra links added to header (after existing nav items)</p>
            </div>

            {/* Current order */}
            <div className="p-5">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Current Extra Header Links</p>
              {headerNav.length === 0 ? (
                <p className="text-[12px] text-gray-400 italic">No extra links added yet.</p>
              ) : (
                <ul className="space-y-2 mb-5">
                  {headerNav.map((item, idx) => (
                    <li key={item.path} className="flex items-center gap-2 bg-pink-50 border border-pink-100 rounded-lg px-3 py-2">
                      <FiMove size={14} className="text-gray-300 shrink-0" />
                      <span className="flex-1 text-[13px] font-semibold text-gray-800 truncate">{item.label}</span>
                      <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{item.path}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveUp(headerNav, setHeaderNav, idx)} className="p-1 text-gray-400 hover:text-gray-700" title="Move up">↑</button>
                        <button onClick={() => moveDown(headerNav, setHeaderNav, idx)} className="p-1 text-gray-400 hover:text-gray-700" title="Move down">↓</button>
                        <button onClick={() => removeFromHeader(item.path)} className="p-1 text-red-400 hover:text-red-600"><FiTrash2 size={13} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Add custom link */}
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-2 mt-4">Add Custom Link</p>
              <div className="flex flex-col gap-2">
                <input value={customHeaderLink.label} onChange={(e) => setCustomHeaderLink((p) => ({ ...p, label: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Link label (e.g. Resources)" />
                <input value={customHeaderLink.path} onChange={(e) => setCustomHeaderLink((p) => ({ ...p, path: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="URL path (e.g. /page/resources)" />
                <button onClick={addCustomHeader} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-pink-50 text-gray-700 hover:text-[#e84fa5] text-[13px] font-semibold rounded-lg transition">
                  <FiPlus size={14} /> Add to Header
                </button>
              </div>
            </div>

            {/* Available pages to toggle */}
            <div className="border-t px-5 py-4">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Add from Your Pages</p>
              <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                {allAvailableItems.map((item) => (
                  <div key={item.path} className="flex items-center gap-3 py-1.5">
                    <button
                      onClick={() => toggleHeader(item)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        isInHeader(item.path) ? "bg-[#e84fa5] border-[#e84fa5]" : "border-gray-300 hover:border-[#e84fa5]"
                      }`}
                    >
                      {isInHeader(item.path) && <FiCheck size={11} className="text-white" />}
                    </button>
                    <span className="text-[13px] text-gray-700 truncate flex-1">{item.label}</span>
                    <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{item.path}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* === FOOTER NAV === */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 border-b px-5 py-4">
              <h2 className="text-[14px] font-extrabold">Footer Navigation</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">Links shown in the footer column</p>
            </div>

            <div className="p-5">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Current Footer Links</p>
              {footerNav.length === 0 ? (
                <p className="text-[12px] text-gray-400 italic">No extra footer links added yet.</p>
              ) : (
                <ul className="space-y-2 mb-5">
                  {footerNav.map((item, idx) => (
                    <li key={item.path} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                      <FiMove size={14} className="text-gray-300 shrink-0" />
                      <span className="flex-1 text-[13px] font-semibold text-gray-800 truncate">{item.label}</span>
                      <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{item.path}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveUp(footerNav, setFooterNav, idx)} className="p-1 text-gray-400 hover:text-gray-700">↑</button>
                        <button onClick={() => moveDown(footerNav, setFooterNav, idx)} className="p-1 text-gray-400 hover:text-gray-700">↓</button>
                        <button onClick={() => removeFromFooter(item.path)} className="p-1 text-red-400 hover:text-red-600"><FiTrash2 size={13} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Add custom footer link */}
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-2 mt-4">Add Custom Link</p>
              <div className="flex flex-col gap-2">
                <input value={customFooterLink.label} onChange={(e) => setCustomFooterLink((p) => ({ ...p, label: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Link label" />
                <input value={customFooterLink.path} onChange={(e) => setCustomFooterLink((p) => ({ ...p, path: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="/page/my-page or /contact" />
                <button onClick={addCustomFooter} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-pink-50 text-gray-700 hover:text-[#e84fa5] text-[13px] font-semibold rounded-lg transition">
                  <FiPlus size={14} /> Add to Footer
                </button>
              </div>
            </div>

            <div className="border-t px-5 py-4">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Add from Your Pages</p>
              <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                {allAvailableItems.map((item) => (
                  <div key={item.path} className="flex items-center gap-3 py-1.5">
                    <button
                      onClick={() => toggleFooter(item)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        isInFooter(item.path) ? "bg-gray-800 border-gray-800" : "border-gray-300 hover:border-gray-600"
                      }`}
                    >
                      {isInFooter(item.path) && <FiCheck size={11} className="text-white" />}
                    </button>
                    <span className="text-[13px] text-gray-700 truncate flex-1">{item.label}</span>
                    <span className="text-[11px] text-gray-400 truncate max-w-[120px]">{item.path}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
            {saving ? "Saving..." : "Save Navigation"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default NavigationManager;
